import { LoaderCircle, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { api, patchJson, postJson } from "./api";
import { confirmNavigation, safeStorage } from "./interaction";
import { Login } from "./components/Login";
import { Masthead, type AppView } from "./components/Masthead";
import { Modal } from "./components/Modal";
import { GlobalSearch, type FlowTarget } from "./components/GlobalSearch";
import { NewBookStudio } from "./components/NewBookStudio";
import { Toast } from "./components/Ui";
import type { AgentConfig, AuthPayload, BootstrapPayload, NovelRun, Project, RunEvent } from "./types";
import { AgentsView } from "./views/AgentsView";
import { BibleView } from "./views/BibleView";
import { ChaptersView } from "./views/ChaptersView";
import { QualityView } from "./views/QualityView";
import { ReaderView } from "./views/ReaderView";
import { WorkspaceView } from "./views/WorkspaceView";

interface ToastState { id: number; message: string; tone: "success" | "error" | "info" }
const activeStatuses = ["queued", "running", "paused", "needs_attention"];
function readRoute(): FlowTarget {
  const parameters = new URLSearchParams(window.location.hash.slice(1));
  const view = parameters.get("view") as AppView;
  const chapter = Number(parameters.get("chapter"));
  return { view: ["workspace", "reader", "chapters", "bible", "agents", "quality"].includes(view) ? view : "workspace", chapter: Number.isInteger(chapter) && chapter > 0 ? chapter : undefined, documentId: parameters.get("document") || undefined, agentId: parameters.get("agent") || undefined };
}
function routeUrl(route: FlowTarget) {
  const parameters = new URLSearchParams({ view: route.view });
  if (route.chapter) parameters.set("chapter", String(route.chapter));
  if (route.documentId) parameters.set("document", route.documentId);
  if (route.agentId) parameters.set("agent", route.agentId);
  return `#${parameters}`;
}

export default function App() {
  const [auth, setAuth] = useState<boolean | null>(null);
  const [data, setData] = useState<BootstrapPayload | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [newBookOpen, setNewBookOpen] = useState(false);
  const [route, setRoute] = useState<FlowTarget>(readRoute);
  const [searchOpen, setSearchOpen] = useState(false);
  const [run, setRun] = useState<NovelRun | null>(null);
  const [events, setEvents] = useState<RunEvent[]>([]);
  const [loadError, setLoadError] = useState("");
  const [authError, setAuthError] = useState("");
  const [sessionExpired, setSessionExpired] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [connectionLost, setConnectionLost] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const [reload, setReload] = useState(0);
  const [toast, setToast] = useState<ToastState | null>(null);
  const toastTimer = useRef<number | null>(null);
  const projectIdRef = useRef("");
  const dataRef = useRef(data); dataRef.current = data;
  const routeRef = useRef(route); routeRef.current = route;
  const requestSequence = useRef(0);
  const navigationLock = useRef(false);
  const historyIndex = useRef(Number(window.history.state?.moyanIndex) || 0);
  const restoringHistory = useRef(false);
  const view = route.view;
  const recordReaderChapter = useCallback((chapter: number) => {
    if (routeRef.current.view !== "reader" || routeRef.current.chapter === chapter) return;
    const target: FlowTarget = { view: "reader", chapter };
    routeRef.current = target; setRoute(target);
    window.history.replaceState({ moyanIndex: historyIndex.current }, "", routeUrl(target));
  }, []);

  const notify = useCallback((message: string, tone: ToastState["tone"] = "info") => {
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    setToast({ id: Date.now(), message, tone });
    // Errors remain available until dismissed; long messages should not disappear before they can be read.
    if (tone !== "error") toastTimer.current = window.setTimeout(() => setToast(null), 6000);
  }, []);
  useEffect(() => () => { if (toastTimer.current) clearTimeout(toastTimer.current); }, []);

  const acceptBootstrap = useCallback((payload: BootstrapPayload) => {
    projectIdRef.current = payload.project.id;
    setData(payload);
    setProjects((current) => current.map((project) => project.id === payload.project.id ? payload.project : project));
    setRun(payload.activeRun);
    safeStorage.setItem("moyan-project-id", payload.project.id);
  }, []);
  const refreshBootstrap = useCallback(async () => {
    if (!projectIdRef.current || navigationLock.current) return;
    const id = projectIdRef.current;
    const sequence = ++requestSequence.current;
    try {
      const payload = await api<BootstrapPayload>(`/api/bootstrap?projectId=${encodeURIComponent(id)}`);
      if (sequence === requestSequence.current && id === projectIdRef.current) acceptBootstrap(payload);
    } catch (error) {
      if (id === projectIdRef.current) notify(error instanceof Error ? `刷新未完成：${error.message}` : "列表刷新失败，已保存的内容不受影响", "error");
    }
  }, [acceptBootstrap, notify]);

  useEffect(() => {
    const controller = new AbortController(); setAuthError("");
    void api<AuthPayload>("/api/auth/status", { signal: controller.signal }).then((result) => { if (!controller.signal.aborted) setAuth(result.authenticated); }).catch((error) => { if (!controller.signal.aborted) setAuthError(error instanceof Error ? error.message : "无法连接服务"); });
    return () => controller.abort();
  }, [reload]);
  useEffect(() => {
    if (!auth || dataRef.current) return;
    const controller = new AbortController(); setLoadError("");
    void (async () => {
      const result = await api<{ projects: Project[] }>("/api/projects", { signal: controller.signal });
      if (controller.signal.aborted) return;
      setProjects(result.projects);
      const remembered = safeStorage.getItem("moyan-project-id");
      const id = result.projects.find((item) => item.id === remembered)?.id ?? result.projects[0]?.id;
      const payload = await api<BootstrapPayload>(`/api/bootstrap${id ? `?projectId=${encodeURIComponent(id)}` : ""}`, { signal: controller.signal });
      if (!controller.signal.aborted) acceptBootstrap(payload);
    })().catch((error) => { if (!controller.signal.aborted) setLoadError(error instanceof Error ? error.message : "数据加载失败"); });
    return () => controller.abort();
  }, [auth, reload, acceptBootstrap]);
  useEffect(() => {
    const expired = () => setSessionExpired(true);
    const syncOnline = () => setOnline(navigator.onLine);
    window.addEventListener("moyan-session-expired", expired);
    window.addEventListener("online", syncOnline); window.addEventListener("offline", syncOnline);
    return () => { window.removeEventListener("moyan-session-expired", expired); window.removeEventListener("online", syncOnline); window.removeEventListener("offline", syncOnline); };
  }, []);

  const runIsActive = Boolean(run && activeStatuses.includes(run.status));
  useEffect(() => {
    if (!run || !runIsActive || sessionExpired) return;
    let closed = false; let inFlight = false; let debounce: number | undefined;
    const id = run.id; const projectId = run.projectId;
    const controller = new AbortController();
    const source = new EventSource(`/api/runs/${encodeURIComponent(id)}/stream`);
    async function sync() {
      if (closed || inFlight) return; inFlight = true;
      try {
        const next = await api<NovelRun>(`/api/runs/${encodeURIComponent(id)}`, { signal: controller.signal });
        if (closed || projectIdRef.current !== projectId) return;
        setRun(next); setConnectionLost(false);
        if (!activeStatuses.includes(next.status)) { source.close(); await refreshBootstrap(); }
      } catch { if (!closed) setConnectionLost(true); }
      finally { inFlight = false; }
    }
    source.onopen = () => setConnectionLost(false);
    source.onmessage = (message) => {
      if (closed || projectIdRef.current !== projectId) return;
      try {
        const event = JSON.parse(message.data) as RunEvent;
        if (!event.id || typeof event.message !== "string") return;
        setEvents((current) => current.some((item) => item.id === event.id) ? current : [...current, event].slice(-500));
        if (debounce === undefined) debounce = window.setTimeout(() => { debounce = undefined; void sync(); }, 600);
      } catch { setConnectionLost(true); }
    };
    // EventSource reconnects are not total connection failures: the HTTP
    // fallback may still be healthy. Verify it before raising the global banner,
    // otherwise every transient stream close moves the controls under the pointer.
    source.onerror = () => { if (!closed) void sync(); };
    const poll = window.setInterval(() => void sync(), 5000);
    return () => { closed = true; controller.abort(); source.close(); clearInterval(poll); clearTimeout(debounce); };
  }, [run?.id, runIsActive, sessionExpired, refreshBootstrap]);

  async function navigate(target: FlowTarget, push = true): Promise<boolean> {
    if (navigationLock.current) return false;
    if (JSON.stringify(target) === JSON.stringify(routeRef.current)) return true;
    navigationLock.current = true;
    try {
      if (!await confirmNavigation()) return false;
      setRoute(target); routeRef.current = target;
      if (push) { historyIndex.current++; window.history.pushState({ moyanIndex: historyIndex.current }, "", routeUrl(target)); }
      return true;
    } finally { navigationLock.current = false; }
  }
  useEffect(() => {
    window.history.replaceState({ moyanIndex: historyIndex.current }, "", routeUrl(routeRef.current));
    const back = async (event: PopStateEvent) => {
      if (restoringHistory.current) { restoringHistory.current = false; return; }
      const nextIndex = Number(event.state?.moyanIndex) || 0;
      if (await navigate(readRoute(), false)) historyIndex.current = nextIndex;
      else if (nextIndex !== historyIndex.current) { restoringHistory.current = true; window.history.go(historyIndex.current - nextIndex); }
      else window.history.replaceState({ moyanIndex: historyIndex.current }, "", routeUrl(routeRef.current));
    };
    window.addEventListener("popstate", back); return () => window.removeEventListener("popstate", back);
  }, []);

  async function selectProject(projectId: string, skipGuard = false): Promise<boolean> {
    if (projectId === projectIdRef.current) return true;
    if (navigationLock.current) return false;
    navigationLock.current = true;
    try {
      if (!skipGuard && !await confirmNavigation()) return false;
      setSwitching(true); ++requestSequence.current;
      const payload = await api<BootstrapPayload>(`/api/bootstrap?projectId=${encodeURIComponent(projectId)}`);
      acceptBootstrap(payload); setEvents([]); setConnectionLost(false); setSearchOpen(false);
      const target: FlowTarget = { view: "workspace" }; setRoute(target); routeRef.current = target;
      window.history.replaceState({ moyanIndex: historyIndex.current }, "", routeUrl(target));
      return true;
    } catch (error) { notify(error instanceof Error ? error.message : "项目切换失败，原项目仍保留", "error"); return false; }
    finally { navigationLock.current = false; setSwitching(false); }
  }
  async function projectCreated(projectId: string) {
    try {
      const result = await api<{ projects: Project[] }>("/api/projects"); setProjects(result.projects);
      if (await selectProject(projectId)) setNewBookOpen(false);
    } catch (error) { notify(error instanceof Error ? error.message : "新书已创建，但项目列表刷新失败，请重试进入", "error"); }
  }
  async function deleteProject(projectId: string) {
    const target = projects.find((project) => project.id === projectId);
    if (projectId === projectIdRef.current && !await confirmNavigation()) throw new Error("已取消删除");
    try {
      await api<void>(`/api/projects/${encodeURIComponent(projectId)}`, { method: "DELETE" });
      const remaining = projects.filter((project) => project.id !== projectId); setProjects(remaining);
      if (projectId === projectIdRef.current) {
        const next = remaining[0];
        if (!next || !await selectProject(next.id, true)) { projectIdRef.current = ""; setData(null); setRun(null); setLoadError("小说已删除，但下一本小说加载失败。请重试加载项目列表。"); }
      }
      notify(`《${target?.title ?? "小说"}》已删除`, "success");
    } catch (error) { notify(error instanceof Error ? error.message : "小说删除失败", "error"); throw error; }
  }
  function acceptRun(next: NovelRun) {
    if (projectIdRef.current !== next.projectId) return;
    setEvents([]); setRun(next);
    setData((current) => current?.project.id === next.projectId ? { ...current, activeRun: next, recentRuns: [next, ...current.recentRuns.filter((item) => item.id !== next.id)] } : current);
  }
  async function startRun(startChapter: number, endChapter: number) {
    const id = projectIdRef.current;
    const next = await postJson<NovelRun>(`/api/projects/${encodeURIComponent(id)}/runs`, { startChapter, endChapter });
    acceptRun(next); notify(`第 ${startChapter}${endChapter === startChapter ? "" : `–${endChapter}`} 章已启动`, "success");
  }
  async function rewriteChapter(chapter: number) {
    const next = await postJson<NovelRun>(`/api/projects/${encodeURIComponent(projectIdRef.current)}/chapters/${chapter}/actions/rewrite`);
    acceptRun(next); notify(`第 ${chapter} 章旧稿已归档，开始全新重写`, "success");
  }
  async function controlRun(action: "pause" | "resume" | "stop") {
    if (!run) return;
    const next = await postJson<NovelRun>(`/api/runs/${encodeURIComponent(run.id)}/control`, { action });
    if (next.projectId === projectIdRef.current) setRun(next);
    notify(action === "pause" ? "暂停请求已提交，正在等待安全停止点" : action === "resume" ? "继续请求已提交" : "停止请求已提交，已保存正文会保留", "success");
    if (action === "stop") await refreshBootstrap();
  }
  async function updateAgent(id: string, patch: Partial<AgentConfig>) {
    const projectId = projectIdRef.current;
    try {
      const next = await patchJson<AgentConfig>(`/api/projects/${encodeURIComponent(projectId)}/agents/${encodeURIComponent(id)}`, patch);
      setData((current) => current?.project.id === projectId ? { ...current, agents: current.agents.map((agent) => agent.id === id ? next : agent) } : current);
    } catch (error) { notify(error instanceof Error ? error.message : "Agent 配置失败", "error"); throw error; }
  }
  async function logout() {
    if (navigationLock.current) return; navigationLock.current = true;
    try {
      if (!await confirmNavigation()) return;
      setSwitching(true); await postJson("/api/auth/logout"); ++requestSequence.current;
      projectIdRef.current = ""; setAuth(false); setData(null); setProjects([]); setRun(null); setEvents([]); setSessionExpired(false); setNewBookOpen(false);
    } catch (error) { notify(error instanceof Error ? error.message : "退出失败，请重试", "error"); }
    finally { navigationLock.current = false; setSwitching(false); }
  }

  const toastNode = toast && <div className="toast-host"><Toast key={toast.id} message={toast.message} tone={toast.tone} onClose={() => setToast(null)} /></div>;
  if (authError || (loadError && !data)) return <><div className="app-error" role="alert"><strong>{authError ? "无法连接创作台" : "创作台加载失败"}</strong><span>{authError || loadError}</span><button className="button button-primary" type="button" onClick={() => { setAuthError(""); setLoadError(""); setReload((value) => value + 1); }}><RefreshCw size={16} />重新连接</button></div>{toastNode}</>;
  if (auth === null) return <div className="app-loading" role="status"><LoaderCircle size={26} className="spin-icon" /><span>正在连接创作台</span></div>;
  if (!auth) return <><Login onSuccess={() => { setAuth(true); setLoadError(""); }} />{toastNode}</>;
  if (!data) return <div className="app-loading" role="status"><LoaderCircle size={26} className="spin-icon" /><span>正在加载小说工程</span></div>;
  return <>
    <div className={`app-shell ${view === "reader" ? "reader-route" : ""}`} inert={switching || undefined}>
      <a href="#main-content" className="ux-skip-link" onClick={(event) => { event.preventDefault(); document.getElementById("main-content")?.focus(); }}>跳到主要内容</a>
      <Masthead view={view} project={data.project} projects={projects} onNavigate={(next) => { void navigate({ view: next }); }} onLogout={() => void logout()} onSearch={() => setSearchOpen(true)} onSelectProject={(id) => { void selectProject(id); }} onDeleteProject={deleteProject} onNewBook={() => setNewBookOpen(true)} />
      {(!online || connectionLost) && <div className="ux-connection-banner" role="status">{!online ? "网络已断开。当前编辑仍保留在页面，请恢复网络后保存；刷新或关闭页面前请先复制重要内容。" : "实时连接暂时中断，正在重连并轮询任务状态。请勿重复启动任务。"}</div>}
      <div className="app-main" id="main-content" tabIndex={-1}>
        <div className="view-container" key={`${data.project.id}:${view}:${view === "reader" ? "" : route.chapter ?? ""}:${route.documentId ?? ""}`}>
          {view === "workspace" && <WorkspaceView initialChapterNumber={route.chapter} data={data} run={run} events={events} onStartRun={startRun} onRewriteChapter={rewriteChapter} onControlRun={controlRun} onChanged={refreshBootstrap} onOpenReader={(chapter) => { void navigate({ view: "reader", chapter }); }} onOpenBible={() => { void navigate({ view: "bible" }); }} notify={notify} />}
          {view === "reader" && <ReaderView onChapterChange={recordReaderChapter} initialChapterNumber={route.chapter} project={data.project} chapters={data.chapters} notify={notify} onExit={(chapter) => { void navigate({ view: "workspace", chapter }); }} />}
          {view === "chapters" && <ChaptersView project={data.project} chapters={data.chapters} globalSearch="" onOpenChapter={(chapter, reading) => { void navigate({ view: reading ? "reader" : "workspace", chapter }); }} />}
          {view === "bible" && <BibleView initialDocumentId={route.documentId} project={data.project} documents={data.documents} ledgers={data.ledgers} globalSearch="" notify={notify} onChanged={refreshBootstrap} />}
          {view === "agents" && <AgentsView initialAgentId={route.agentId} data={data} onUpdateAgent={updateAgent} onChanged={refreshBootstrap} notify={notify} />}
          {view === "quality" && <QualityView quality={data.quality} agents={data.agents} runs={data.recentRuns} threshold={data.settings.scoreThreshold} />}
        </div>
      </div>
      <NewBookStudio key={data.project.id} open={newBookOpen} sourceProject={data.project} onClose={() => setNewBookOpen(false)} onProjectCreated={projectCreated} notify={notify} />
    </div>
    {switching && <div className="ux-switching-overlay" role="status"><LoaderCircle className="spin-icon" />正在切换，当前内容仍保留</div>}
    <GlobalSearch key={data.project.id} open={searchOpen} data={data} onClose={() => setSearchOpen(false)} onChoose={navigate} />
    <Modal open={sessionExpired} title="登录已过期" description="当前编辑仍保留，重新登录后继续保存。" onClose={() => setSessionExpired(false)}><Login onSuccess={() => { setSessionExpired(false); notify("已重新登录，当前编辑未被覆盖", "success"); }} /></Modal>
    {toastNode}
  </>;
}
